import React, { createContext, useContext, useState, useEffect } from "react";
import { createGlobalStyle } from "@/lib/theme";

/**
 * Canvas integration for Zapier Interfaces
 *
 * This module handles iframe communication when Interfaces is embedded in Canvas.
 * When Canvas wants to focus on an interface, it sends a message that hides
 * the surrounding UI (header, sidebar, navigation) for a cleaner embedded experience.
 */

interface CanvasContextType {
  isRenderedInCanvas: boolean;
}

interface IframeReadyMessage {
  event: "iframe-ready";
}

interface MessageData {
  [key: string]: unknown;
  type: string;
}

const isMessageData = (data: unknown): data is MessageData =>
  typeof data === "object" && data !== null && "type" in data;

const CanvasContext = createContext<CanvasContextType | undefined>(undefined);

// Global styles for Canvas focus mode
const CanvasGlobalStyle = createGlobalStyle`
  /* Canvas focus mode: Visual indicator for read-only state */
  [data-isrenderedincanvas="true"],
  [data-isrenderedincanvas="true"] * {
    cursor: not-allowed !important;
  }
`;

/**
 * Sets up Canvas read-only mode using event delegation
 * @returns cleanup function to remove all event listeners
 */
export function setupCanvasReadOnlyMode() {
  const preventCanvasInteractions = (event: Event) => {
    // Allow scroll events to pass through
    if (event.type === "wheel" || event.type === "scroll") {
      return;
    }

    // Check if event happened within a Canvas element
    const target = event.target as Element;
    const canvasElement = target.closest('[data-isrenderedincanvas="true"]');

    if (canvasElement) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const eventTypes = [
    "click",
    "mousedown",
    "mouseup",
    "keydown",
    "keyup",
    "focus",
  ];

  // Single set of listeners on document (event delegation)
  eventTypes.forEach((eventType) => {
    document.addEventListener(eventType, preventCanvasInteractions, true);
  });

  // Return cleanup function
  return () => {
    eventTypes.forEach((eventType) => {
      document.removeEventListener(eventType, preventCanvasInteractions, true);
    });
  };
}

/**
 * Provider that manages Canvas integration state and iframe communication.
 *
 * Handles:
 * - Listening for Canvas focus mode messages
 * - Sending iframe ready signals to parent
 * - Managing embedded vs standalone UI state
 * - Automatically applying read-only behavior when in Canvas focus mode
 */
export function CanvasFocusProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isRenderedInCanvas, setIsRenderedInCanvas] = useState(false);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        isMessageData(event.data) &&
        event.data.type === "canvas:focus-mode"
      ) {
        setIsRenderedInCanvas(true);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Global Canvas read-only behavior using event delegation
  useEffect(() => {
    if (!isRenderedInCanvas) return;
    return setupCanvasReadOnlyMode();
  }, [isRenderedInCanvas]);

  return (
    <CanvasContext.Provider value={{ isRenderedInCanvas }}>
      {isRenderedInCanvas ? <CanvasGlobalStyle /> : null}
      {children}
    </CanvasContext.Provider>
  );
}

/**
 * Hook to check if the interface is currently rendered in Canvas focus mode.
 *
 * When true, the interface should hide surrounding UI elements (header, sidebar, etc.)
 * to provide a cleaner embedded experience within Canvas.
 *
 * @returns {boolean} isRenderedInCanvas - Whether the interface is in Canvas focus mode
 */
export function useIsRenderedInCanvas() {
  const context = useContext(CanvasContext);
  if (context === undefined) {
    return { isRenderedInCanvas: false };
  }
  return context;
}

/**
 * Hook that sends an iframe ready signal to the parent window when called.
 *
 * This signals to Canvas that the interface has finished loading and is ready
 * for interaction. Should be called when the page/component is fully rendered.
 */
export function useIframeReady() {
  useEffect(() => {
    try {
      if (window.parent && window.parent !== window) {
        const message: IframeReadyMessage = { event: "iframe-ready" };
        window.parent.postMessage(message, "*");
      }
    } catch (error) {
      console.warn("Failed to send iframe ready signal:", error);
    }
  }, []);
}
